import React from 'react';
import type { CSSProperties } from 'react';
import type { CardDieCut } from '@apps/card/routes/CardCustomizer/components/CardDieCut';
import { PPI } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { cardDieCutToShapeConfig } from '@apps/card/utils';
import { FitCardBox, FitFrame, FullWidthCardBox } from './PreviewThumbnail.styles.internal';

/** The font size the die-cut's `em` radii resolve against on the live page. */
const ROOT_FONT_SIZE_PX = 16;

/**
 * The die-cut's corners as percentages of the card, so they hold at whatever size the image renders.
 *
 * The live card applies the shape's `em` radii to the unscaled page (`inches * PPI`), where CSS
 * shrinks any pair of radii that overruns a side (an arch's two top corners meet in a semicircle),
 * and then scales the whole page down. The static image isn't scaled, so the result is reproduced
 * here: clamp the radii the way CSS does, then express them against the page's size.
 */
export const getCardCornerRadii = (shape: CardDieCut, widthInches: number, heightInches: number): string => {
  const width = widthInches * PPI;
  const height = heightInches * PPI;
  const corners = cardDieCutToShapeConfig(shape).borderRadius.split(' ');
  // Top-left, top-right, bottom-right, bottom-left; a single value covers all four.
  const [topLeft, topRight, bottomRight, bottomLeft] = [0, 1, 2, 3].map(index => {
    const value = corners[index] ?? corners[0];
    return parseFloat(value) * (value.endsWith('em') ? ROOT_FONT_SIZE_PX : 1);
  });

  const fitsSide = (side: number, first: number, second: number) => (first + second > side ? side / (first + second) : 1);
  const clamp = Math.min(
    fitsSide(width, topLeft, topRight),
    fitsSide(width, bottomLeft, bottomRight),
    fitsSide(height, topLeft, bottomLeft),
    fitsSide(height, topRight, bottomRight),
  );

  const radii = [topLeft, topRight, bottomRight, bottomLeft].map(radius => radius * clamp);
  const asPercentOf = (side: number) => radii.map(radius => `${(radius / side) * 100}%`).join(' ');
  return `${asPercentOf(width)} / ${asPercentOf(height)}`;
};

type PreviewThumbnailProps = Readonly<{
  src: string;
  alt: string;
  cardShape: CardDieCut;
  widthInches: number;
  heightInches: number;
  /** The live preview's scaler: `contain` fits both axes as `FitScaler` does, `width` fills the width as `PageScaler` does. */
  fit: 'contain' | 'width';
  /** `FitScaler`'s `padding`, in px. */
  padding?: number;
}>;

/**
 * The design's static image, standing in for the live card while it can't render yet. Sized, cut and
 * shadowed like the card it stands in for, in CSS alone, so the server render is right before any
 * script runs and the swap doesn't move the card.
 */
export const PreviewThumbnail = ({ src, alt, cardShape, widthInches, heightInches, fit, padding = 0 }: PreviewThumbnailProps) => {
  const isLandscape = widthInches > heightInches;
  const cardFit = widthInches === heightInches ? 0.8 : 1;
  const cardBoxStyle = {
    '--card-ratio': widthInches / heightInches,
    '--card-fit': cardFit,
    '--card-fallback-width': isLandscape ? `${cardFit * 100}%` : 'auto',
    '--card-fallback-height': isLandscape ? 'auto' : `${cardFit * 100}%`,
    borderRadius: getCardCornerRadii(cardShape, widthInches, heightInches),
  } as CSSProperties;

  const image = (
    <img
      src={src}
      alt={alt}
      // Lowercase so React passes it through as a plain attribute: this is the page's hero image.
      {...({ fetchpriority: 'high' } as Record<string, string>)}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );

  if (fit === 'width') {
    return <FullWidthCardBox style={cardBoxStyle}>{image}</FullWidthCardBox>;
  }

  return (
    <FitFrame style={{ padding }}>
      <FitCardBox style={cardBoxStyle}>{image}</FitCardBox>
    </FitFrame>
  );
};
